import React from 'react';
import { FormSection } from '@atlaskit/form';
import { generatedTypesNew, Maybe, PaymentMethod } from '@css-xp/graphtypes';
import { NOT_AVAILABLE } from '@atlassian/cssxp-utils-entitlement';
import { capitalize, compact, defaultTo } from 'lodash';
import { getInvoiceGroupName } from '../../transfer-entitlement/src/progress-forms/billing-profile-table';
import './transferred-account-summary-details.scss';
import {
  CreditCard,
  PaymentMethodType,
  PaymentOnTerms,
  SortDirection,
  EntitlementDetails,
} from '@css-xp/graphtypes/src/generated-types-new';
import { SubscriptionTransferReasonCodes } from '@atlassian/cssxp-types-entitlement';
import { useQuery } from '@apollo/client';
import { fetchOrdersForEntitlement } from '../../../../entitlement-page/src/queries/fetch-orders-for-entitlement';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { fetchBillingProfilesForTransactionAccount, fetchTransactionAccount } from '@css-xp/billing-v2';
import { ErrorComponents } from '@css-xp/base';

interface Props {
  entitlementId: string;
  scheduledChanges?: EntitlementDetails['scheduledChanges'];
}

const formatPaymentMethod = (paymentMethod?: PaymentMethod) => {
  if (paymentMethod?.type === PaymentMethodType.CARD) {
    const creditCard = paymentMethod as CreditCard;
    return (
      <>
        &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; {creditCard?.card?.last4Numbers}
        {', '} Expires {('0' + creditCard?.card?.expirationMonth).slice(-2)}/
        {creditCard?.card?.expirationYear?.slice(-2)}
      </>
    );
  }
  if (paymentMethod?.type === PaymentMethodType.DEFERRED) {
    const paymentOnTerms = paymentMethod as PaymentOnTerms;
    return <span>Payment on terms: due in {paymentOnTerms.terms?.invoiceDueDays} days</span>;
  }
};

export const TransferredAccountDetails = ({ entitlementId, scheduledChanges }: Props) => {
  const [showMore, setShowMore] = React.useState<boolean>(false);

  const { data, error, loading: orderDataLoading } = useQuery<
    generatedTypesNew.FetchOrdersForEntitlementQuery,
    generatedTypesNew.FetchOrdersForEntitlementQueryVariables
  >(fetchOrdersForEntitlement, {
    variables: {
      id: entitlementId,
      searchOptions: {
        first: 1,
        sort: { sortFields: ['createdDate', 'id'], sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC] },
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const transferEntitlementData = data?.entitlementDetails?.orders?.nodes[0]?.metadataObj;
  const reasonCode = transferEntitlementData?.reasonCode;

  const { data: billingAccountData, loading: billingAccountLoading } = useQuery<
    generatedTypesNew.FetchTransactionAccountQuery,
    generatedTypesNew.FetchTransactionAccountQueryVariables
  >(fetchTransactionAccount, {
    variables: {
      id: scheduledChanges?.transactionAccountId ?? '',
    },
    fetchPolicy: 'cache-and-network',
  });

  const selectedBillingAccount = billingAccountData?.transactionAccount;

  const { data: billingProfileData, loading: billingProfileLoading } = useQuery<
    generatedTypesNew.FetchBillingProfilesForTransactionAccountQuery,
    generatedTypesNew.FetchBillingProfilesForTransactionAccountQueryVariables
  >(fetchBillingProfilesForTransactionAccount, {
    variables: { id: scheduledChanges?.transactionAccountId ?? '' },
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-and-network',
    skip: !scheduledChanges?.transactionAccountId,
  });

  const billingProfiles = billingProfileData?.transactionAccount.billingProfiles?.nodes;
  const selectedBillingProfile = billingProfiles?.find((ele: any) => ele?.id === scheduledChanges?.invoiceGroupId);

  const formatAddress = () => {
    const address: string[] = [];
    address.push(defaultTo(selectedBillingAccount?.billToParty?.addressLine1, ''));
    address.push(defaultTo(selectedBillingAccount?.billToParty?.addressLine2, ''));
    address.push(defaultTo(selectedBillingAccount?.billToParty?.city, ''));
    address.push(
      defaultTo(selectedBillingAccount?.billToParty?.state + ' ' + selectedBillingAccount?.billToParty?.postcode, '')
    );
    address.push(defaultTo(selectedBillingAccount?.billToParty?.country, ''));

    const billingAccountAddress = defaultTo(compact(address).join(', '), ' - ');

    return billingAccountAddress;
  };

  const billingAdmin = selectedBillingAccount?.billingAdmins?.nodes?.[0]?.email;
  const billingAdminNodes = selectedBillingAccount?.billingAdmins?.nodes;

  const transferedReasonCode = reasonCode
    ? SubscriptionTransferReasonCodes[reasonCode as keyof typeof SubscriptionTransferReasonCodes]
    : 'N/A';

  const showMoreBillingAdmins = () => {
    return billingAdminNodes?.map((ele, index) => {
      if (index !== 0) {
        return <div key={index}>{ele?.email}</div>;
      }
    });
  };

  const renderBillingAdmins = () => {
    const length = selectedBillingAccount?.billingAdmins?.nodes?.length ?? 0;
    const firstAdmin = billingAdmin;

    if (billingAdminNodes?.length === 1) {
      return <>{firstAdmin}</>;
    }
    return (
      <>
        {' '}
        {firstAdmin} &nbsp; &nbsp;
        <a onClick={() => setShowMore(!showMore)} style={{ cursor: 'pointer' }} data-testid="show-more-email">
          {!showMore && <span data-testid="show-email">+{length - 1} more</span>}
          {showMore && <span data-testid="hide-email">Hide</span>}
        </a>
        {showMore && showMoreBillingAdmins()}
      </>
    );
  };

  if (orderDataLoading || billingAccountLoading || billingProfileLoading) return <LoadingIndicator />;
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  return (
    <>
      <FormSection>
        <label className={'scheduled-label'}>Reason code</label>
        <div className={'disabled-div'}>{transferedReasonCode}</div>
      </FormSection>
      <div data-testid="transferred-account-details">
        <FormSection>
          <label className={'scheduled-label'}>Billing account</label>
          <div className={'disabled-div'}>
            <div className={'fw-7'}>
              {selectedBillingAccount?.displayName} ({selectedBillingAccount?.transactionAccountNumber})
            </div>
            <div>{selectedBillingAccount?.id ? <>{formatAddress()}</> : 'Not available'}</div>
            <div className={'field-spacing'}>
              <div>
                Billing admins: <span data-testid="billing-admins">{renderBillingAdmins()}</span>
              </div>
              <div>Tax ID: {selectedBillingAccount?.billToParty?.taxId ?? 'Not provided'}</div>
            </div>
            <div>
              Pricing:{' '}
              {selectedBillingAccount?.billToPartyPricingEligibilty ? (
                capitalize(selectedBillingAccount?.billToPartyPricingEligibilty)
              ) : (
                <span aria-label="Not applicable">{NOT_AVAILABLE}</span>
              )}
            </div>
          </div>
        </FormSection>
      </div>

      <div data-testid="transferred-profile-details">
        <FormSection>
          <label className={'scheduled-label'}>Billing profile</label>
          <div className={'disabled-div'}>
            <div className={'fw-7'}>{getInvoiceGroupName(selectedBillingProfile?.name)}</div>
            <div>{selectedBillingProfile?.id ? <>{formatAddress()}</> : 'Not available'}</div>
            <div className={'field-spacing'} data-testid="payment-method">
              Credit card:{' '}
              {selectedBillingProfile?.defaultPaymentMethod
                ? formatPaymentMethod(selectedBillingProfile?.defaultPaymentMethod)
                : 'Not available'}
              <div>Tax ID: {selectedBillingProfile?.shipToParty?.taxId ?? 'Not provided'}</div>
            </div>
            <div>
              Pricing:{' '}
              {selectedBillingProfile?.shipToPriceEligibility ? (
                capitalize(selectedBillingProfile?.shipToPriceEligibility)
              ) : (
                <span aria-label="Not applicable">{NOT_AVAILABLE}</span>
              )}
            </div>
            <div>Currency: {selectedBillingProfile?.currency}</div>
          </div>
        </FormSection>
      </div>
    </>
  );
};
